import React, { FC } from "react";

interface Props {
  dayOfMonth: number;
  date?: Date | undefined;
  excludedDays?: string[];
  unavailableDays?: string[];
  availabilityData?: {
    [date: string]: {
      hasSlots: boolean;
      priceRange?: { min: number; max: number };
      popularity?: "high" | "medium" | "low";
      isBlocked?: boolean;
    };
  };
}

const DatePickerCustomDay: FC<Props> = ({ dayOfMonth, date, excludedDays, unavailableDays, availabilityData }) => {
  if (!date) return <span>{dayOfMonth}</span>;

  const formattedDate = formatDate(date);
  const isExcluded = excludedDays?.includes(formattedDate);
  const isUnavailable = unavailableDays?.includes(formattedDate);
  const dayData = availabilityData?.[formattedDate];
  const isBlocked = dayData?.isBlocked || isUnavailable;

  // Vérifier si la date est dans le passé
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
  const currentDate = new Date(date);
  currentDate.setHours(0, 0, 0, 0);
  const isPastDate = currentDate < today;

  // Vérifier si la date a des créneaux disponibles
  const hasAvailableSlots = dayData?.hasSlots === true;
  // Vérifier si la date n'a explicitement aucune disponibilité
  const hasNoAvailability = (dayData && dayData.hasSlots === false) || isUnavailable;

  // Classes de base
  let dayClass = "react-datepicker__day_span relative cursor-pointer";

  if (isPastDate) {
    // Jours passés : grisés et non cliquables
    dayClass += " react-datepicker__day_span_past bg-gray-100 cursor-not-allowed opacity-50";
  } else if (isExcluded) {
    dayClass += " react-datepicker__day_span_unavailable";
  } else if (hasNoAvailability) {
    // Dates sans availability ou indisponibles : gris comme les jours passés
    dayClass += " react-datepicker__day_span_no_availability bg-gray-100 cursor-not-allowed opacity-50";
  } else if (isBlocked) {
    // Dates bloquées par le propriétaire : rouge
    dayClass += " react-datepicker__day_span_blocked bg-red-100 text-red-800 hover:bg-red-200";
  } else if (hasAvailableSlots) {
    // Dates avec availability : vert
    dayClass += " react-datepicker__day_span_available bg-green-50 text-green-800 hover:bg-green-100";
  } else {
    // Dates futures sans données de disponibilité : gris plus clair
    dayClass += " react-datepicker__day_span_neutral bg-gray-50 text-gray-600 hover:bg-gray-100";
  }

  // Déterminer le style inline pour forcer la couleur du texte
  const textStyle = isPastDate || hasNoAvailability ? { color: "#9CA3AF" } : {};

  return (
    <span className={dayClass} style={textStyle}>
      {dayOfMonth}
      {/* Indicateur de disponibilité - seulement pour les dates avec disponibilité */}
      {hasAvailableSlots && !isExcluded && !isUnavailable && !isPastDate && (
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex space-x-0.5">
          <div className="w-1 h-1 bg-eventToBee rounded-full"></div>
          {dayData.popularity === "high" && (
            <>
              <div className="w-1 h-1 bg-eventToBee rounded-full"></div>
              <div className="w-1 h-1 bg-eventToBee rounded-full"></div>
            </>
          )}
        </div>
      )}
      {/* Indicateur de prix (optionnel) - seulement pour les dates avec disponibilité */}
      {dayData?.priceRange && hasAvailableSlots && !isExcluded && !isUnavailable && !isPastDate && (
        <div className="absolute -top-1 -right-1 text-xs bg-eventToBee text-white rounded-full w-4 h-4 flex items-center justify-center opacity-75">
          €
        </div>
      )}
    </span>
  );
};

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default DatePickerCustomDay;
